// ═══════════════════════════════════════════════════════════════════════════
// drpCardRules.js — the DRP farmer card's quarter rules, ONE copy for the screen
// (DRPView in App.jsx) and tools/check-drp-card.js.
//
//   drpCoverageTotals    — ACTIVE vs all-time endorsements and liability. The
//                          header and the stat cards read the same numbers.
//   drpQuarterStatus     — active / awaiting payment / paid / settled, per
//                          (quarter × DRP crop year) cell. An ended quarter with
//                          no paid claim and an estimate still owed stays on the
//                          main list as "Ended — awaiting payment".
//   drpTriggerDistance   — the quarter's expected class price against the
//                          lbs-weighted trigger, in plain words.
//   drpSeasonSummary     — per DRP crop year: producer premium, claims paid,
//                          estimated still to come, net.
//   drpCoveredMilkLimit  — % insured against RMA's covered-milk limit.
//
// COVERED-MILK LIMIT IS RMA's, NOT OURS. M13 Handbook P28-1 (Plan 83 Indemnity
// Calculation), "Covered Milk Production":
//   Round(Min(Total Producer Declared Production, Total Milk Marketings / 0.85)
//         × Declared Covered Milk Production / Total Producer Declared Production, 0)
// So milk is covered in full only while marketings reach 85% of the declared
// production; below that RMA scales it to marketings ÷ 0.85. Our tables carry
// the declared COVERED lbs (P18 #28) and not the Total Producer Declared
// Production, so the card measures against the insured lbs and says so.
//
// Every estimate this module touches is an internal ADMIN figure (claim
// estimates are admin-only — Kevin, 2026-10-18). The caller gates; the module
// computes.
// ═══════════════════════════════════════════════════════════════════════════
"use strict";

const COVERED_MILK_MARKETINGS_FACTOR = 0.85; // P28-1 Plan 83, Covered Milk Production

const cents = (x) => Math.round(Number(x) * 100);
const money2 = (c) => "$" + (Math.abs(c) / 100).toFixed(2);
const lbsText = (n) => { const v = Number(n) || 0; return v >= 1e6 ? (v / 1e6).toFixed(1) + "M lb" : Math.round(v / 1000) + "K lb"; };

// "Q3 2026" -> "2026-09-30"; null when the label is not a quarter.
function drpQuarterEndIso(label) {
  const m = String(label || "").trim().match(/^Q([1-4])\s+(\d{4})$/);
  if (!m) return null;
  return m[2] + "-" + ["03-31", "06-30", "09-30", "12-31"][Number(m[1]) - 1];
}

// An endorsement is ACTIVE until its quarter has ended. An unparseable quarter
// falls back to end_date; truly unknown reads active (unchanged rule).
function drpDateIso(d) {
  if (!d) return null;
  const s = String(d);
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return iso[1] + "-" + iso[2] + "-" + iso[3];
  const mdy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (mdy) return mdy[3] + "-" + mdy[1].padStart(2, "0") + "-" + mdy[2].padStart(2, "0");
  return null;
}
function drpIsActiveEnd(e, todayIso) {
  const end = drpQuarterEndIso(e && e.quarter) || drpDateIso(e && e.endDate);
  return end ? end >= todayIso : true;
}

// The ONE count/liability rule for the header AND the stat cards.
function drpCoverageTotals(ends, todayIso) {
  const sum = (rows, k) => rows.reduce((s, e) => s + (Number(e[k]) || 0), 0);
  const all = ends || [];
  const active = all.filter((e) => drpIsActiveEnd(e, todayIso));
  return {
    active: { count: active.length, liability: sum(active, "liability"), lbs: sum(active, "declaredMilkProd") },
    all: { count: all.length, liability: sum(all, "liability"), lbs: sum(all, "declaredMilkProd") },
  };
}

// One (quarter × crop year) cell. `paid` = dollars of paid claims for that crop
// year attributed to the quarter (null = none). `est` = drpEstimateIndemnity's
// result. `yafPending` = endorsements whose RMA yield adjustment is pending.
// A cell still OWES an answer while it is unpaid and either priced in the money,
// not priced at all, or waiting on RMA's yield adjustment (a YAF below 1 can
// trigger a quarter whose price sat above the trigger).
function drpCellAwaiting(cell) {
  if (!cell || cell.paid != null) return false;
  const est = cell.est;
  if (!est || est.amount == null) return true;
  if (est.amount > 0) return true;
  return (Number(cell.yafPending) || 0) > 0;
}

function drpQuarterStatus({ ended, cells }) {
  const list = cells || [];
  const paidAny = list.some((c) => c.paid != null);
  if (!ended) return { state: "active", awaiting: [], paidAny };
  const awaiting = list.filter(drpCellAwaiting);
  if (awaiting.length) return { state: "awaiting", awaiting, paidAny };
  return { state: paidAny ? "paid" : "settled", awaiting: [], paidAny };
}

// Which quarter rows the Summary shows by default. Active always; an ended
// quarter still awaiting payment only where estimates may be shown (admin) —
// in the portal "awaiting payment" would itself be a claim estimate.
function drpQuarterVisible(status, { showAll, showEstimates }) {
  if (showAll) return true;
  if (status.state === "active") return true;
  return status.state === "awaiting" && !!showEstimates;
}

// The quarter's expected class price against the triggers of that class.
// rows: endorsements of ONE class; price: the quarter's expected class price.
function drpTriggerDistance(rows, price) {
  const list = (rows || []).filter((e) => Number(e.triggerPrice) > 0 && Number(e.declaredMilkProd) > 0);
  if (!list.length) return null;
  const lbs = list.reduce((s, e) => s + Number(e.declaredMilkProd), 0);
  const trigC = Math.round(list.reduce((s, e) => s + cents(e.triggerPrice) * Number(e.declaredMilkProd), 0) / lbs);
  const trigs = list.map((e) => cents(e.triggerPrice));
  const out = { n: list.length, lbs, triggerC: trigC, minC: Math.min(...trigs), maxC: Math.max(...trigs), priceC: null, gapC: null, paying: [] };
  if (price == null || !Number.isFinite(Number(price))) return out;
  out.priceC = cents(price);
  out.gapC = out.priceC - trigC;
  // An endorsement is in the money when the quarter price sits BELOW its trigger.
  const byTrig = {};
  for (const e of list) {
    const t = cents(e.triggerPrice);
    if (out.priceC < t) { byTrig[t] = (byTrig[t] || 0) + Number(e.declaredMilkProd); }
  }
  out.paying = Object.keys(byTrig).map(Number).sort((a, b) => b - a).map((t) => ({ triggerC: t, lbs: byTrig[t] }));
  out.payingN = list.filter((e) => out.priceC < cents(e.triggerPrice)).length;
  return out;
}

function drpTriggerDistanceText(cls, d) {
  if (!d) return null;
  const name = "Class " + cls;
  if (d.priceC == null) return name + " — not priced yet";
  const p = money2(d.priceC);
  if (d.minC === d.maxC) {
    const t = money2(d.minC);
    if (d.priceC < d.minC) return `${name} ${p} vs trigger ${t} — paying ${money2(d.minC - d.priceC)}/cwt`;
    if (d.priceC > d.minC) return `${name} ${p} vs trigger ${t} — ${money2(d.priceC - d.minC)} above`;
    return `${name} ${p} vs trigger ${t} — at the trigger`;
  }
  const range = `${name} ${p} vs triggers ${money2(d.minC)}–${money2(d.maxC)}`;
  if (!d.payingN) return `${range} — ${money2(d.priceC - d.maxC)} above the highest`;
  if (d.payingN === d.n) return `${range} — all ${d.n} paying, ${money2(d.minC - d.priceC)}/cwt below the lowest`;
  const which = d.paying.slice(0, 3).map((x) => `${money2(x.triggerC)} (${lbsText(x.lbs)})`).join(", ");
  return `${range} — ${d.payingN} of ${d.n} paying: ${which}${d.paying.length > 3 ? ", …" : ""}`;
}

// Per DRP crop year. ends carry {cropYear, producerPremium}; claims carry
// {cropYear, amount} (paid indemnity rows); cells are the quarter × crop-year
// cells with {cropYear, state, paid, est} where state is the quarter's.
function drpSeasonSummary({ ends, claims, cells }) {
  const by = {};
  const row = (cy) => (by[cy] = by[cy] || { cropYear: cy, premiumC: 0, paidC: 0, toComeC: 0, unpriced: 0 });
  for (const e of ends || []) { if (e.cropYear == null) continue; row(e.cropYear).premiumC += cents(Number(e.producerPremium) || 0); }
  for (const c of claims || []) { if (c.cropYear == null || !(Number(c.amount) > 0)) continue; row(c.cropYear).paidC += cents(c.amount); }
  for (const c of cells || []) {
    if (c.cropYear == null) continue;
    const r = row(c.cropYear);
    const owed = c.state === "active" || (c.state === "awaiting" && drpCellAwaiting(c));
    if (!owed || c.paid != null) continue;
    if (!c.est || c.est.amount == null) { r.unpriced += 1; continue; }
    r.toComeC += cents(c.est.amount);
  }
  return Object.values(by).sort((a, b) => a.cropYear - b.cropYear).map((r) => ({
    cropYear: r.cropYear, premium: r.premiumC / 100, paid: r.paidC / 100, toCome: r.toComeC / 100,
    net: (r.paidC + r.toComeC - r.premiumC) / 100, unpriced: r.unpriced,
  }));
}

// % insured against RMA's covered-milk limit. insuredLbs = the quarter's
// declared covered lbs; estProduction = the production (marketings) the agent
// entered for the quarter.
function drpCoveredMilkLimit(insuredLbs, estProduction) {
  const L = Number(insuredLbs) || 0, M = Number(estProduction) || 0;
  if (!(L > 0) || !(M > 0)) return null;
  const pct = (L / M) * 100;
  const limitPct = 100 / COVERED_MILK_MARKETINGS_FACTOR;
  const coveredLbs = Math.min(L, M / COVERED_MILK_MARKETINGS_FACTOR);
  const within = pct <= limitPct + 1e-9;
  // At or under 100% there is nothing to explain; above it, the headroom is what matters.
  const text = pct <= 100 ? null : within
    ? `Over 100% but within RMA's covered-milk limit: ${(limitPct - pct).toFixed(1)} pts of headroom (limit ${limitPct.toFixed(1)}% = marketings ÷ 0.85)`
    : `Above RMA's covered-milk limit (${limitPct.toFixed(1)}%): at this production RMA covers about ${lbsText(coveredLbs)} (${(coveredLbs / L * 100).toFixed(1)}% of insured)`;
  return { pct, limitPct, within, coveredLbs, coveredShare: coveredLbs / L, headroomPct: limitPct - pct, text,
    basis: "M13 P28-1 Plan 83: covered = min(declared production, marketings ÷ 0.85); measured against insured lbs — total declared production is not in our data" };
}

module.exports = {
  COVERED_MILK_MARKETINGS_FACTOR,
  drpQuarterEndIso, drpDateIso, drpIsActiveEnd, drpCoverageTotals,
  drpCellAwaiting, drpQuarterStatus, drpQuarterVisible,
  drpTriggerDistance, drpTriggerDistanceText,
  drpSeasonSummary, drpCoveredMilkLimit,
};
